/**
 * Pure jump and fall physics model.
 *
 * No Babylon.js imports, no side effects, no navigator coupling.
 * Two core functions for player vertical velocity shaping:
 * - applyJumpCut: scales upward velocity on button release (short-hop mechanic)
 * - shapeFall: adds extra downward acceleration during descent (fast-fall)
 *
 * Also exports resolveMovementProfile — pure helper that merges per-cat overrides
 * with the shared PLAYER.* defaults. Put here alongside the other pure movement
 * helpers to keep this module the single source of truth for movement math.
 */

/**
 * Apply jump-cut mechanics: reduce upward velocity when jump button is released early.
 *
 * @param vy Current vertical velocity (positive = up, negative = down)
 * @param justReleased True if the jump button was just released this frame
 * @param cutMult Multiplier to apply to vy when cutting (e.g., 0.45 for 45% of original height)
 * @returns The modified (or unmodified) vy value
 *
 * Semantics:
 * - If `vy > 0` (ascending) AND `justReleased`, multiply vy by cutMult
 * - Otherwise, return vy unchanged (cutMult=1 is always a no-op)
 */
export function applyJumpCut(
  vy: number,
  justReleased: boolean,
  cutMult: number
): number {
  if (vy > 0 && justReleased) {
    return vy * cutMult;
  }
  return vy;
}

/**
 * Shape fall with extra downward acceleration (fast-fall mechanic).
 *
 * @param vy Current vertical velocity (positive = up, negative = down)
 * @param dt Delta time in seconds (e.g., 1/60 for 60 FPS)
 * @param fallAccel Extra downward acceleration to apply (world units/s²)
 * @param maxFall Maximum downward speed (magnitude; e.g., 15 to cap at -15 m/s)
 * @returns The modified (or unmodified) vy value
 *
 * Semantics:
 * - If `vy < 0` (descending):
 *   1. Subtract fallAccel * dt from vy (makes it more negative)
 *   2. Clamp the result so it never goes below -maxFall
 * - If `vy >= 0` (ascending or at apex), return vy unchanged
 */
export function shapeFall(
  vy: number,
  dt: number,
  fallAccel: number,
  maxFall: number
): number {
  if (vy < 0) {
    // Apply extra downward acceleration
    let newVy = vy - fallAccel * dt;
    // Clamp to terminal velocity
    return Math.max(newVy, -maxFall);
  }
  return vy;
}

/**
 * Per-cat movement profile — the resolved set of values used inside applyInput.
 *
 * Each field is the per-cat override when present, otherwise the shared PLAYER.*
 * constant. The air-acceleration composition (`airAcceleration * airAccelerationScale`)
 * is deliberately NOT done here — the caller owns that multiplication so the
 * existing `config.airAccelerationScale` semantics are unchanged.
 */
export interface MovementProfile {
  moveSpeed: number;
  jumpImpulse: number;
  acceleration: number;
  deceleration: number;
  /** Raw air acceleration before multiplying by config.airAccelerationScale. */
  airAcceleration: number;
  airDeceleration: number;
  maxFallSpeed: number;
}

/**
 * Shape of the PLAYER.* defaults object accepted by resolveMovementProfile.
 * Matches the subset of PLAYER constants used in applyInput movement math.
 */
export interface PlayerDefaults {
  MOVE_SPEED: number;
  JUMP_IMPULSE: number;
  ACCELERATION: number;
  DECELERATION: number;
  AIR_ACCELERATION: number;
  AIR_DECELERATION: number;
  MAX_FALL_SPEED: number;
}

/**
 * Shape of the per-cat config overrides accepted by resolveMovementProfile.
 * All fields are optional; omitting a field falls back to the PLAYER.* default.
 */
export interface MovementOverrides {
  moveSpeed?: number;
  jumpImpulse?: number;
  acceleration?: number;
  deceleration?: number;
  airAcceleration?: number;
  airDeceleration?: number;
  maxFallSpeed?: number;
}

/**
 * Resolve a per-cat movement profile.
 *
 * Returns each value as `overrides.X ?? defaults.X`. When no overrides are
 * supplied (Minna/Basil in their untuned state) the result is byte-identical
 * to reading PLAYER.* directly.
 *
 * @param overrides - Per-cat config subset (all optional).
 * @param defaults  - Shared PLAYER constants object.
 */
export function resolveMovementProfile(
  overrides: MovementOverrides,
  defaults: PlayerDefaults
): MovementProfile {
  return {
    moveSpeed:       overrides.moveSpeed       ?? defaults.MOVE_SPEED,
    jumpImpulse:     overrides.jumpImpulse     ?? defaults.JUMP_IMPULSE,
    acceleration:    overrides.acceleration    ?? defaults.ACCELERATION,
    deceleration:    overrides.deceleration    ?? defaults.DECELERATION,
    airAcceleration: overrides.airAcceleration ?? defaults.AIR_ACCELERATION,
    airDeceleration: overrides.airDeceleration ?? defaults.AIR_DECELERATION,
    maxFallSpeed:    overrides.maxFallSpeed    ?? defaults.MAX_FALL_SPEED,
  };
}
